import { useSelector, useDispatch } from "react-redux";
import { fetchCurrency } from "../actions/currencyAction";
import { EUR_TO_FCFA_RATE, USD_TO_FCFA_RATE } from "../reducers/currencyReducer";

/** Retourne la devise utilisateur (string) - rétrocompatible */
export function useCurrency() {
	const currencyState = useSelector((state) => state.currency);
	return currencyState?.currency || "";
}

/** Retourne la devise + fonctions de conversion EUR ↔ FCFA ↔ USD */
export function useCurrencyConversion() {
	const currencyState = useSelector((state) => state.currency);
	const currency = currencyState?.currency || "";
	const eurToFcfaRate = currencyState?.eurToFcfaRate ?? EUR_TO_FCFA_RATE;
	const usdToFcfaRate = currencyState?.usdToFcfaRate ?? USD_TO_FCFA_RATE;

	const normalizeCurrency = (c) => (c === "DOLLAR" ? "USD" : c);

	return {
		currency,
		eurToFcfaRate,
		usdToFcfaRate,
		/** Convertit FCFA → EUR */
		convertFcfaToEur: (fcfa) => (parseFloat(fcfa) || 0) / eurToFcfaRate,
		/** Convertit FCFA → USD */
		convertFcfaToUsd: (fcfa) => (parseFloat(fcfa) || 0) / usdToFcfaRate,
		/** Convertit EUR → FCFA */
		convertEurToFcfa: (eur) => (parseFloat(eur) || 0) * eurToFcfaRate,
		/** Convertit USD → FCFA */
		convertUsdToFcfa: (usd) => (parseFloat(usd) || 0) * usdToFcfaRate,
		/** Convertit une valeur vers la devise cible (FCFA, Euro, USD) */
		convert: (value, fromCurrency, toCurrency) => {
			const v = parseFloat(value) || 0;
			const from = normalizeCurrency(fromCurrency);
			const to = normalizeCurrency(toCurrency);
			if (from === to) return v;
			let inFcfa = v;
			if (from === "Euro") inFcfa = v * eurToFcfaRate;
			else if (from === "USD") inFcfa = v * usdToFcfaRate;
			if (to === "FCFA") return inFcfa;
			if (to === "Euro") return inFcfa / eurToFcfaRate;
			if (to === "USD") return inFcfa / usdToFcfaRate;
			return inFcfa;
		},
		convertFromFcfa: (fcfaValue, targetCurrency) => {
			const v = parseFloat(fcfaValue) || 0;
			const to = normalizeCurrency(targetCurrency);
			if (to === "Euro") return v / eurToFcfaRate;
			if (to === "USD") return v / usdToFcfaRate;
			return v;
		},
		convertToFcfa: (value, sourceCurrency) => {
			const v = parseFloat(value) || 0;
			const from = normalizeCurrency(sourceCurrency);
			if (from === "Euro") return v * eurToFcfaRate;
			if (from === "USD") return v * usdToFcfaRate;
			return v;
		},
		/** Vérifie si USD est une devise utilisée (Euro, FCFA, DOLLAR, USD) */
		hasUsd: () => ["USD", "DOLLAR", "Dollar"].includes(currency)
	};
}

export function useFetchCurrency() {
	const dispatch = useDispatch();
	const fetchCurrencyData = () => dispatch(fetchCurrency());
	return fetchCurrencyData;
}